// Layouts/Home/Dashboard.tsx
// GET /v1/leads-activity/stats
// GET /v1/leads-activity/recent-activity?limit=5
// GET /v1/leads-activity/recently-contacted?limit=5&page=1
//
// Layout, not a port of the mobile screen:
//   - one bordered header strip carrying all four headline numbers (no repeated
//     boxed stat cards)
//   - hero panel: donut of the stage split + a ranked list of the same stages
//     with progress bars, mirroring the reference image's donut+ranked-list pair
//   - activity feed rendered as a timeline (connecting rail), contacted leads
//     rendered as a plain roster — two different list treatments, not the same
//     row component reused twice
//   - team activity panel (status row + agent roster in one card) + weekly
//     calls chart + follow-up checklist: UI-enhancement panels backed by dummy
//     data (clearly marked below), built with the same Panel/SectionHead/color
//     tokens as the live sections
//   - Panel now stretches to fill its grid row (flex:1 on SHELL/INNER) so two
//     side-by-side panels with different content always render the same height
import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from 'recharts';
import {
  Phone,
  FileText,
  Users,
  AlarmClock,
  Activity,
  RefreshCw,
  ChevronRight,
  CloudOff,
  TrendingUp,
  Wifi,
  PhoneCall,
  Coffee,
  CalendarX,
  CheckSquare,
  Square,
} from 'lucide-react';
import { G, C } from '../components/GlobalComponents/uiTokens';
import apiClient from '../stateManagers/apiSetup/axiosInterceptor';

// ── TYPES ─────────────────────────────────────────────────────────────────────
interface LeadStats {
  new: number;
  contacted: number;
  closed: number;
  not_interested: number;
  total: number;
}
type StageKey = keyof Omit<LeadStats, 'total'>;

interface ActivityItem {
  lead_id: string;
  name: string | null;
  status: string | null;
  latest_activity: {
    type: string;
    text: string | null;
    outcome: string | null;
    extra_note: string | null;
    created_at: string | null;
  } | null;
}

interface ContactedLead {
  lead_id: string;
  name: string | null;
  phone: string | null;
  source: string | null;
  status: string | null;
  city: string | null;
  last_call: { text: string | null; outcome: string | null; called_at: string | null } | null;
}

// ── SHELL / INNER (matches LeadDetailView / Sidebar chrome — grey outer border) ─
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  boxSizing: 'border-box',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
  flex: 1,
  minHeight: 0,
  boxSizing: 'border-box',
};
const Panel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={SHELL}>
    <div style={{ ...INNER, ...style }}>{children}</div>
  </div>
);

const STAGES: { key: StageKey; label: string; color: string }[] = [
  { key: 'new', label: 'New', color: '#3b82f6' },
  { key: 'contacted', label: 'Contacted', color: '#f59e0b' },
  { key: 'closed', label: 'Closed', color: '#22c55e' },
  { key: 'not_interested', label: 'Not interested', color: '#ef4444' },
];

const ACTIVITY_TYPES: Record<string, { icon: any; color: string; label: string }> = {
  call_log: { icon: Phone, color: '#2563eb', label: 'Call logged' },
  note: { icon: FileText, color: '#6b8e12', label: 'Note added' },
  meeting: { icon: Users, color: '#c2700c', label: 'Meeting' },
  follow_up: { icon: AlarmClock, color: '#7c3aed', label: 'Follow-up' },
};
const DEFAULT_TYPE = { icon: Activity, color: C.teal, label: 'Activity' };

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  new: { bg: 'rgba(59,130,246,0.1)', color: '#1d4ed8' },
  contacted: { bg: 'rgba(245,158,11,0.12)', color: '#b45309' },
  closed: { bg: 'rgba(22,163,74,0.12)', color: '#15803d' },
  not_interested: { bg: 'rgba(239,68,68,0.1)', color: '#c62828' },
};
const statusStyle = (s: string | null) => STATUS_STYLES[s?.toLowerCase() ?? ''] ?? { bg: 'rgba(156,163,175,0.12)', color: '#4b5563' };

const AVATAR_COLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777'];
const avc = (s: string | null) => {
  if (!s) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const initialsOf = (name: string | null) => {
  const clean = (name ?? '').trim();
  if (!clean) return '?';
  const p = clean.split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : clean.slice(0, 2)).toUpperCase();
};
const relativeTime = (value: string | null) => {
  if (!value) return '';
  let iso = value.includes('T') ? value : value.replace(' ', 'T');
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) iso += 'Z';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// ── DUMMY DATA (UI enhancement only — swap for real endpoints later) ───────────
// Team snapshot strip
const DUMMY_TEAM_STATUS = [
  { label: 'Online', value: 5, icon: Wifi, color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  { label: 'On call', value: 2, icon: PhoneCall, color: '#2563eb', bg: 'rgba(37,99,235,0.1)' },
  { label: 'Idle', value: 1, icon: Coffee, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  { label: 'On leave', value: 1, icon: CalendarX, color: '#7c3aed', bg: 'rgba(124,58,237,0.1)' },
];

// Calls this week
const DUMMY_WEEKLY_CALLS = [
  { day: 'Mon', calls: 14 },
  { day: 'Tue', calls: 21 },
  { day: 'Wed', calls: 18 },
  { day: 'Thu', calls: 26 },
  { day: 'Fri', calls: 22 },
  { day: 'Sat', calls: 9 },
  { day: 'Sun', calls: 4 },
];
const DUMMY_WEEKLY_TOTAL = DUMMY_WEEKLY_CALLS.reduce((sum, d) => sum + d.calls, 0);

// Today's follow-ups
const DUMMY_FOLLOWUPS = [
  { id: 1, name: 'Rohan Malhotra', note: 'Confirm admission fee schedule', done: true },
  { id: 2, name: 'Simran Kaur', note: 'Share brochure for grade 9', done: true },
  { id: 3, name: 'Aditya Verma', note: 'Callback after 5 PM', done: false },
  { id: 4, name: 'Fatima Sheikh', note: 'Send campus visit slot', done: false },
  { id: 5, name: 'Karan Thakur', note: 'Follow up on pending docs', done: false },
];

// Agents at work
const DUMMY_AGENTS = [
  { name: 'Ayesha Khan', minutes: 260, capMinutes: 480 },
  { name: 'Devraj Singh', minutes: 200, capMinutes: 480 },
  { name: 'Neha Sharma', minutes: 140, capMinutes: 480 },
  { name: 'Imran Qureshi', minutes: 95, capMinutes: 480 },
];
const fmtHrs = (mins: number) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

// ── fetch hook ─────────────────────────────────────────────────────────────
function useDashboardFetch<T>(url: string, params?: Record<string, any>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiClient.get(url, { params });
      if (res.data.success) setData(res.data.data as T);
      else setError(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, JSON.stringify(params ?? {})]);

  return { data, loading, error, reload: load };
}

// ── small atoms ────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ icon: any; title: string; hint?: string }> = ({ icon: Icon, title, hint }) => (
  <div style={{ textAlign: 'center', padding: '34px 22px' }}>
    <div style={{ width: 44, height: 44, borderRadius: 13, background: C.limeDim, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 9.2px' }}>
      <Icon size={20} color={C.teal} />
    </div>
    <div style={{ fontSize: '0.81rem', fontWeight: 700, color: C.teal }}>{title}</div>
    {hint && <div style={{ fontSize: '0.74rem', color: C.muted, marginTop: 4 }}>{hint}</div>}
  </div>
);

const ErrorState: React.FC<{ what: string; onRetry: () => void }> = ({ what, onRetry }) => (
  <div style={{ textAlign: 'center', padding: '34px 22px' }}>
    <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 9.2px' }}>
      <CloudOff size={20} color="#c62828" />
    </div>
    <div style={{ fontSize: '0.81rem', fontWeight: 700, color: C.teal }}>Couldn't load {what}</div>
    <button
      onClick={onRetry}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5.5, marginTop: 11, padding: '6.5px 14px', background: 'rgba(17,85,80,0.07)', border: 'none', borderRadius: 20, color: C.teal, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
    >
      <RefreshCw size={12} /> Try again
    </button>
  </div>
);

const StatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  const s = statusStyle(status);
  return (
    <span style={{ padding: '1.8px 7.4px', borderRadius: 20, fontSize: '0.62rem', fontWeight: 700, textTransform: 'capitalize', background: s.bg, color: s.color }}>
      {status ? status.replace(/_/g, ' ') : 'Unknown'}
    </span>
  );
};

const SectionHead: React.FC<{ title: string; pill?: string; onPill?: () => void; tag?: string }> = ({ title, pill, onPill, tag }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '24px 0 9px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7.4 }}>
      <div style={{ width: 3, height: 14.8, background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: 3 }} />
      <span style={{ fontSize: '0.86rem', fontWeight: 700, color: C.teal }}>{title}</span>
      {tag && (
        <span style={{ fontSize: '0.6rem', fontWeight: 700, color: C.muted, background: '#eef2f1', padding: '2px 7px', borderRadius: 20 }}>{tag}</span>
      )}
    </div>
    {pill && (
      <button
        onClick={onPill}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 2.8, padding: '4.6px 11px', background: 'rgba(17,85,80,0.07)', border: 'none', borderRadius: 20, color: C.teal, fontSize: '0.71rem', fontWeight: 700, cursor: 'pointer' }}
      >
        {pill} <ChevronRight size={11} />
      </button>
    )}
  </div>
);

// ── MAIN ──────────────────────────────────────────────────────────────────
interface DashboardProps {
  onNavigateToLeads?: () => void;
  onOpenLead?: (leadId: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigateToLeads, onOpenLead }) => {
  const { data: stats, loading: statsLoading, error: statsError, reload: reloadStats } =
    useDashboardFetch<LeadStats>('/v1/leads-activity/stats');
  const { data: activity, loading: activityLoading, error: activityError, reload: reloadActivity } =
    useDashboardFetch<ActivityItem[]>('/v1/leads-activity/recent-activity', { limit: 5 });
  const { data: contacted, loading: contactedLoading, error: contactedError, reload: reloadContacted } =
    useDashboardFetch<ContactedLead[]>('/v1/leads-activity/recently-contacted', { limit: 5, page: 1 });

  const [followups, setFollowups] = useState(DUMMY_FOLLOWUPS);
  const toggleFollowup = (id: number) =>
    setFollowups((prev) => prev.map((f) => (f.id === id ? { ...f, done: !f.done } : f)));
  const doneCount = followups.filter((f) => f.done).length;

  const total = stats?.total ?? 0;
  const closedRate = stats && total > 0 ? Math.round((stats.closed / total) * 100) : 0;
  const donutData = STAGES.map((s) => ({ name: s.label, value: stats?.[s.key] ?? 0, color: s.color })).filter((d) => d.value > 0);

  return (
    <div>
      {/* ── HEADLINE STRIP: one bordered card, four numbers, dividers ── */}
      <Panel style={{ padding: '18px 22px', display: 'flex', alignItems: 'stretch' }}>
        {statsLoading ? (
          <div style={{ height: 40, width: '100%', borderRadius: 8, background: '#eef2f1' }} />
        ) : (
          [
            { label: 'Total leads', value: total },
            { label: 'New', value: stats?.new ?? 0 },
            { label: 'Contacted', value: stats?.contacted ?? 0 },
            { label: 'Closed rate', value: `${closedRate}%`, accent: true },
          ].map((m, i) => (
            <React.Fragment key={m.label}>
              {i > 0 && <div style={{ width: 1, alignSelf: 'stretch', background: G.line, margin: '0 26px' }} />}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{m.label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '1.28rem', fontWeight: 700, color: C.teal, letterSpacing: '-0.3px' }}>{m.value}</span>
                  {m.accent && <TrendingUp size={14} color="#15803d" />}
                </div>
              </div>
            </React.Fragment>
          ))
        )}
      </Panel>

      {/* ── HERO: donut + ranked stage list, side by side ── */}
      <SectionHead title="Pipeline breakdown" pill="View all leads" onPill={onNavigateToLeads} />
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 11 }}>
        <Panel style={{ padding: '18px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {statsLoading ? (
            <div style={{ width: 150, height: 150, borderRadius: '50%', background: '#eef2f1' }} />
          ) : !stats || total === 0 ? (
            <EmptyState icon={Activity} title="No leads yet" hint="Your pipeline split will show up here." />
          ) : (
            <div style={{ position: 'relative', width: 170, height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donutData} dataKey="value" innerRadius={58} outerRadius={78} paddingAngle={3} stroke="none">
                    {donutData.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: C.teal, letterSpacing: '-0.4px' }}>{total}</span>
                <span style={{ fontSize: '0.63rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Total</span>
              </div>
            </div>
          )}
        </Panel>

        <Panel style={{ padding: '9px 4px' }}>
          {statsError && !stats ? (
            <ErrorState what="lead stats" onRetry={reloadStats} />
          ) : !stats || total === 0 ? (
            <EmptyState icon={Activity} title="Nothing to rank yet" />
          ) : (
            STAGES.map((s, i) => {
              const count = stats[s.key] ?? 0;
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div
                  key={s.key}
                  style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 18px', borderBottom: i < STAGES.length - 1 ? `1px solid ${G.softer}` : 'none' }}
                >
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1f2937', width: 118, flexShrink: 0 }}>{s.label}</span>
                  <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#eef2f1', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: s.color, borderRadius: 3, transition: 'width 0.5s ease' }} />
                  </div>
                  <span style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, width: 34, textAlign: 'right', flexShrink: 0 }}>{count}</span>
                  <span style={{ fontSize: '0.66rem', color: C.muted, width: 32, textAlign: 'right', flexShrink: 0 }}>{pct}%</span>
                </div>
              );
            })
          )}
        </Panel>
      </div>

      {/* ── ACTIVITY FEED (timeline rail) + CONTACTED ROSTER (plain list) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHead title="Recent activity" />
          <Panel style={{ padding: '16px 18px' }}>
            {activityLoading ? (
              <div style={{ height: 140, borderRadius: 8, background: '#eef2f1' }} />
            ) : activityError && !activity ? (
              <ErrorState what="recent activity" onRetry={reloadActivity} />
            ) : !activity || activity.length === 0 ? (
              <EmptyState icon={Activity} title="No activity yet" hint="Calls, notes and follow-ups appear here." />
            ) : (
              activity.map((item, i) => {
                const act = item.latest_activity;
                const type = ACTIVITY_TYPES[act?.type ?? ''] ?? DEFAULT_TYPE;
                const Icon = type.icon;
                const detail = act?.outcome || act?.text || act?.extra_note || '';
                const name = item.name?.trim() || 'Unnamed lead';
                const when = relativeTime(act?.created_at ?? null);
                const isLast = i === activity.length - 1;

                return (
                  <div key={item.lead_id} style={{ display: 'flex', gap: 11 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 22, flexShrink: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: type.color, marginTop: 4, boxShadow: `0 0 0 3px ${type.color}1f` }} />
                      {!isLast && <div style={{ width: 1.5, flex: 1, background: G.line, minHeight: 30, marginTop: 3 }} />}
                    </div>
                    <div
                      onClick={() => onOpenLead?.(item.lead_id)}
                      style={{ flex: 1, paddingBottom: isLast ? 4 : 16, cursor: onOpenLead ? 'pointer' : 'default' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon size={12} color={type.color} />
                        <span style={{ fontSize: '0.71rem', fontWeight: 700, color: type.color }}>{type.label}</span>
                        {when && <span style={{ fontSize: '0.64rem', color: C.muted, marginLeft: 'auto' }}>{when}</span>}
                      </div>
                      <div style={{ fontSize: '0.79rem', fontWeight: 600, color: '#1f2937', margin: '3px 0 2px' }}>{name}</div>
                      {detail && (
                        <div style={{ fontSize: '0.7rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</div>
                      )}
                      <div style={{ marginTop: 5 }}>
                        <StatusBadge status={item.status} />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </Panel>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHead title="Recently contacted" />
          <Panel style={{ padding: '4px 0' }}>
            {contactedLoading ? (
              <div style={{ height: 140, borderRadius: 8, background: '#eef2f1', margin: 14 }} />
            ) : contactedError && !contacted ? (
              <ErrorState what="recent calls" onRetry={reloadContacted} />
            ) : !contacted || contacted.length === 0 ? (
              <EmptyState icon={Phone} title="No calls logged yet" hint="Leads you call and log show up here." />
            ) : (
              contacted.map((lead, i) => {
                const name = lead.name?.trim() || 'Unnamed lead';
                const outcome = lead.last_call?.outcome || lead.last_call?.text;
                const fallback = [lead.source, lead.city].filter(Boolean).join(' · ');
                const when = relativeTime(lead.last_call?.called_at ?? null);

                return (
                  <div
                    key={lead.lead_id}
                    onClick={() => onOpenLead?.(lead.lead_id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '11px 18px',
                      borderBottom: i < contacted.length - 1 ? `1px solid ${G.softer}` : 'none',
                      cursor: onOpenLead ? 'pointer' : 'default',
                    }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        background: avc(name),
                        color: '#fff',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {initialsOf(name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                      <div style={{ fontSize: '0.68rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{outcome || fallback || 'No details'}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                      <StatusBadge status={lead.status} />
                      {when && <span style={{ fontSize: '0.62rem', color: C.muted }}>{when}</span>}
                    </div>
                    {lead.phone && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`tel:${lead.phone!.replace(/[^\d+]/g, '')}`);
                        }}
                        style={{ width: 30, height: 30, borderRadius: 9, background: C.limeDim, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                      >
                        <Phone size={13} color="#4a5e00" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </Panel>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          UI ENHANCEMENT — dummy data below, same tokens/patterns as above.
          Wire each panel to a real endpoint when one exists.
      ══════════════════════════════════════════════════════════════════ */}

      {/* ── TEAM ACTIVITY: one panel — status row on top, roster below,
             split by an internal rule instead of two separate floating cards ── */}
      <SectionHead title="Team activity" tag="Sample data" />
      <Panel style={{ padding: 0 }}>
        <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'stretch' }}>
          {DUMMY_TEAM_STATUS.map((s, i) => {
            const Icon = s.icon;
            return (
              <React.Fragment key={s.label}>
                {i > 0 && <div style={{ width: 1, alignSelf: 'stretch', background: G.line, margin: '0 26px' }} />}
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={16} color={s.color} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.66rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{s.label}</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: C.teal }}>{s.value}</div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ borderTop: `1px solid ${G.line}`, padding: '5px 4px 9px' }}>
          {DUMMY_AGENTS.map((a, i) => {
            const pct = Math.round((a.minutes / a.capMinutes) * 100);
            return (
              <div
                key={a.name}
                style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '9px 18px', borderBottom: i < DUMMY_AGENTS.length - 1 ? `1px solid ${G.softer}` : 'none' }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: avc(a.name),
                    color: '#fff',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {initialsOf(a.name)}
                </div>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1f2937', width: 130, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#eef2f1', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: C.lime, borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: C.teal, width: 62, textAlign: 'right', flexShrink: 0 }}>{fmtHrs(a.minutes)}</span>
              </div>
            );
          })}
        </div>
      </Panel>

      {/* ── WEEKLY CALLS CHART + FOLLOW-UP CHECKLIST ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 11 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHead title="Calls this week" tag="Sample data" />
          <Panel style={{ padding: '18px 20px', display: 'flex', gap: 20, alignItems: 'center' }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Total calls</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: C.teal, letterSpacing: '-0.3px' }}>{DUMMY_WEEKLY_TOTAL}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, color: '#15803d', fontSize: '0.68rem', fontWeight: 700 }}>
                <TrendingUp size={12} /> +12% vs last week
              </div>
            </div>
            <div style={{ flex: 1, height: 130 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={DUMMY_WEEKLY_CALLS} barCategoryGap="32%">
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10.5, fill: C.muted }} />
                  <Tooltip cursor={{ fill: 'rgba(17,85,80,0.05)' }} contentStyle={{ borderRadius: 8, border: `1px solid ${G.line}`, fontSize: '0.72rem' }} />
                  <Bar dataKey="calls" radius={[5, 5, 0, 0]} fill={C.lime} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHead title="Today's follow-ups" tag="Sample data" pill={`${doneCount}/${followups.length} done`} />
          <Panel style={{ padding: '4px 0' }}>
            {followups.map((f, i) => (
              <div
                key={f.id}
                onClick={() => toggleFollowup(f.id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 18px',
                  borderBottom: i < followups.length - 1 ? `1px solid ${G.softer}` : 'none',
                  cursor: 'pointer',
                  opacity: f.done ? 0.55 : 1,
                }}
              >
                {f.done ? <CheckSquare size={16} color={C.teal} style={{ marginTop: 1, flexShrink: 0 }} /> : <Square size={16} color={C.muted} style={{ marginTop: 1, flexShrink: 0 }} />}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#1f2937', textDecoration: f.done ? 'line-through' : 'none' }}>{f.name}</div>
                  <div style={{ fontSize: '0.68rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.note}</div>
                </div>
              </div>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;